"use client";
import { useZendeskSsoRedirect } from "@/components/hooks/use-zendesk-sso-redirect";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/components/ui/use-toast";
import { DISABLE_BUTTON_DURATION_MS } from "@/constants";
import {
  type LoginError,
  type LoginFormData,
} from "@/server/actions/employers/login/login";
import { pagesPath } from "@/utils/pages-path";
import { zodResolver } from "@hookform/resolvers/zod";
import EyeIcon from "@public/icons/eye-icon.svg";
import EyeOffIcon from "@public/icons/eye-off-icon.svg";
import TextLogo from "@public/text-logo.svg";
import Link from "next/link";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { MdCancel } from "react-icons/md";
import { z } from "zod";
import { useLogin } from "./hooks/use-login";

const loginFailureMessage = (error: LoginError): string => {
  switch (error) {
    case "invalidLoginCredentials":
    case "validationError":
      return "ログインに失敗しました。入力内容に誤りがないかご確認ください。";
    case "invalidUserKind":
      return "ログインに失敗しました。下部の「加入者の方はこちらからログイン」を選択してからもう一度お試し下さい。";
    default:
      return "ログインに失敗しました。時間をおいてもう一度お試しいただくか、サポートにお問い合わせください。";
  }
};

type LoginPageProps = {
  redirect?: string;
};

export const LoginPage = ({ redirect }: LoginPageProps) => {
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();
  const [passwordMaskEnabled, setPasswordMaskEnabled] = useState<boolean>(true);
  const { isZendeskSso, returnTo } = useZendeskSsoRedirect(redirect);

  const formSchema = z.object({
    email: z.string().min(1, {
      message: "メールアドレスが入力されていません。",
    }),
    password: z.string().min(1, {
      message: "パスワードが入力されていません。",
    }),
  });
  const form = useForm<LoginFormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const login = useLogin();

  const onSubmit = form.handleSubmit(async (data) => {
    setSubmitting(true);
    const error = await login(data);
    if (error) {
      setTimeout(() => setSubmitting(false), DISABLE_BUTTON_DURATION_MS); // 連打対策
      toast({
        title: loginFailureMessage(error),
        variant: "error",
      });
    }
  });

  return (
    <div className="m-6 flex h-screen flex-col items-center md:mt-24">
      <Form {...form}>
        <form onSubmit={onSubmit} className="w-full max-w-lg">
          <h1>
            <TextLogo aria-label="VCスタートアップ健康保険組合" />
          </h1>
          <Separator className="my-6 bg-monotone-2" />
          <div className="flex flex-col gap-6">
            <h2 className="text-lg font-bold">事業所ログイン</h2>
            <div className="flex flex-col gap-6">
              <FormField
                name="email"
                control={form.control}
                render={({ field, fieldState }) => (
                  <FormItem className="relative">
                    <FormLabel className="font-bold">
                      メールアドレス：
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder="メールアドレス"
                        autoComplete="email"
                        type="email"
                        isInvalid={fieldState.invalid}
                        {...field}
                      />
                    </FormControl>
                    <div
                      className="absolute top-6 right-1 p-3"
                      onClick={() => {
                        form.setValue("email", "");
                      }}
                      role="button"
                      aria-label="入力したメールアドレスをクリア"
                    >
                      <MdCancel className="fill-monotone-6 stroke-white" />
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                name="password"
                control={form.control}
                render={({ field, fieldState }) => (
                  <FormItem className="relative">
                    <FormLabel className="font-bold">パスワード：</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="パスワード"
                        autoComplete="current-password"
                        type={passwordMaskEnabled ? "password" : "text"}
                        isInvalid={fieldState.invalid}
                        {...field}
                      />
                    </FormControl>
                    <div
                      className="absolute top-6 right-0 inline-flex h-10 w-12 items-center justify-center p-2"
                      role="button"
                      onClick={() => {
                        setPasswordMaskEnabled((p) => !p);
                      }}
                      aria-label={
                        passwordMaskEnabled
                          ? "パスワードを隠す"
                          : "パスワードを表示する"
                      }
                    >
                      {passwordMaskEnabled ? (
                        <EyeIcon style={{ color: "hsl(var(--primary-500))" }} />
                      ) : (
                        <EyeOffIcon
                          style={{ color: "hsl(var(--primary-500))" }}
                        />
                      )}
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="w-full text-right">
                <Link
                  href={pagesPath.employers.forgot_password.$url().path}
                  className="underline"
                >
                  パスワード再発行
                </Link>
              </div>
            </div>
            <Button
              type="submit"
              variant="primary"
              size="lg"
              className="mt-4 w-full"
              disabled={submitting}
            >
              ログイン
            </Button>

            <div className="w-full text-center">
              <Link
                href={
                  isZendeskSso && returnTo
                    ? pagesPath.members.zendesk.sso.$url({
                        query: { return_to: returnTo },
                      }).path
                    : pagesPath.members.login.$url().path
                }
                className="underline"
              >
                加入者の方はこちらからログイン
              </Link>
            </div>
          </div>
        </form>
      </Form>
    </div>
  );
};
